import React, { useEffect, useState } from "react";
import { MapPin, PackageCheck, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const KEY = "hb_postcode";
const fmt = (iso, withMonth = true) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.toLocaleDateString("en-AU", { weekday: "short" })} ${d.getDate()}${withMonth ? ` ${d.toLocaleDateString("en-AU", { month: "short" })}` : ""}`;
};

export default function DeliveryEstimate({ slug }) {
  const [postcode, setPostcode] = useState(() => localStorage.getItem(KEY) || "");
  const [est, setEst] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = async (pc) => {
    if (!/^\d{4}$/.test(pc)) { setErr("Enter a 4-digit Australian postcode"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/products/${slug}/delivery-estimate`, { params: { postcode: pc } });
      setEst(data); localStorage.setItem(KEY, pc);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); setEst(null); }
    finally { setBusy(false); }
  };

  useEffect(() => { setEst(null); if (/^\d{4}$/.test(postcode)) lookup(postcode); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const sameMonth = est && est.earliest.slice(0, 7) === est.latest.slice(0, 7);
  return (
    <div className="mt-5 rounded-2xl bg-white border-2 border-[#19324A]/8 p-4" data-testid="delivery-estimate">
      <form onSubmit={(e) => { e.preventDefault(); lookup(postcode); }} className="flex items-center gap-2">
        <MapPin className="w-5 h-5 text-[#4CC9F0] shrink-0" />
        <label htmlFor="delivery-postcode" className="text-sm font-semibold text-[#19324A] whitespace-nowrap">Arrives by?</label>
        <input id="delivery-postcode" inputMode="numeric" maxLength={4} value={postcode} onChange={(e) => setPostcode(e.target.value.replace(/\D/g, ""))} placeholder="Postcode"
          className="w-24 rounded-xl border-2 border-[#19324A]/10 px-3 py-1.5 text-sm outline-none focus:border-[#4CC9F0]" data-testid="delivery-postcode" />
        <button type="submit" disabled={busy} className="rounded-full bg-[#19324A] text-white text-xs font-bold px-3 py-2 disabled:opacity-60" data-testid="delivery-check">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check"}</button>
      </form>
      {err && <p className="mt-2 text-xs text-[#B91C1C]" data-testid="delivery-error">{err}</p>}
      {est && (
        <div className="mt-3 flex items-start gap-2" data-testid="delivery-result">
          <PackageCheck className="w-5 h-5 text-[#7ED957] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-display font-bold text-[#19324A]" data-testid="delivery-window">
              Arrives {sameMonth ? `${fmt(est.earliest, false)} – ${fmt(est.latest)}` : `${fmt(est.earliest)} – ${fmt(est.latest)}`} to {est.postcode}
            </p>
            <p className="text-xs text-[#19324A]/60">
              {est.fulfilment === "printify" ? `Made to order (${est.handling_days} business days)` : est.fulfilment === "in_house" ? `Personalised by hand here (${est.handling_days} business days)` : "Ships next business day"}
              {" "}+ {est.transit_days[0]}–{est.transit_days[1]} days {est.zone} delivery
            </p>
            {est.cutoff && (
              <p className={`mt-1 text-xs font-semibold ${est.cutoff.passed || !est.cutoff.in_time ? "text-[#B91C1C]" : "text-[#166534]"}`} data-testid="delivery-cutoff">
                {est.cutoff.passed ? `The ${est.cutoff.label} cut-off has passed — this will arrive after Christmas` : est.cutoff.in_time ? `✓ In time for ${est.cutoff.label} if you order today` : `Cutting it fine — may arrive after Christmas to ${est.postcode}`}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
